import Image from "next/image";

/**
 * Spatialist.xyz logo mark. Single component, consistent presence across
 * the app. Variants control how loudly the mark asserts itself — most
 * in-app surfaces use "watermark" or "subtle" for an unobtrusive brand
 * signature (think Figma's "F" in a canvas corner).
 *
 * ── What "wordmark" means in this codebase ────────────────────────
 * The wordmark renders "SPATIALIST" from hand-curated Nasalization SVG
 * paths (`public/brand/wordmark-*.svg`) plus a live-text lowercase
 * `.xyz` suffix in Nasalization (Typekit / FontFace). The icon is the
 * red cube at `public/logo-icon.png`. Horizontal / stacked lockups
 * composite icon + wordmark at runtime.
 *
 * ── `.xyz` suffix (founder 2026-07-03) ─────────────────────────────
 * Height is 63% of the wordmark cap-height. Always lowercase — never
 * inherit a parent `uppercase`. Optical centering is baked in: the
 * lockup is shifted left by half the suffix width so the NAME sits on
 * the container's centerline and `.xyz` hangs to the right (not
 * patched per call site).
 *
 * ── Theme awareness ────────────────────────────────────────────────
 * Wordmark SVGs ship in two variants: `-light.svg` (black fill, for
 * light backgrounds) and `-dark.svg` (white fill, for dark ones). Both
 * are rendered and toggled via the `.dark` class on <html> using
 * Tailwind's `dark:` variant — no `useTheme()`/`mounted` dance, no
 * flash on hydrate. `forceTheme` overrides when the parent surface has
 * a fixed color (e.g. the login page's always-dark hero). The `.xyz`
 * text follows the same theme colors.
 *
 * The icon layout is theme-invariant (red on anything) so it renders
 * a single <Image> regardless.
 */
interface LogoMarkProps {
  /**
   * Which composition to render.
   *   - `icon` — red cube only (default; theme-invariant)
   *   - `horizontal` — icon + wordmark on one line (theme-aware)
   *   - `stacked` — icon above wordmark (theme-aware)
   *   - `wordmark` — wordmark only, no icon (theme-aware)
   */
  layout?: "icon" | "horizontal" | "stacked" | "wordmark";
  /**
   * Which brand wordmark to render (naming canon v2, AD #158 — the
   * SpaceManager / SpaceStation apps share the identity system).
   *   - `spatialist` — SPATIALIST SVG paths + `.xyz` suffix (default)
   *   - `spacemanager` — SPACEMANAGER as live Nasalization text,
   *     theme-aware black/white (black+red on light, white+red on dark
   *     — the red comes from the cube mark in lockups). SpaceManager is
   *     intentionally not a domain, so it NEVER takes a `.xyz` suffix
   *     regardless of `withXyz`.
   *   - `spacestation` — dual-tone SPACE + STATION live Nasalization
   *     text (no `.xyz`; SpaceStation is internal-only display name).
   *     SPACE uses brand red; STATION is black on light / white on dark
   *     (design-language black+red / white+red pairing).
   * The `icon` layout is brand-invariant (one red cube system).
   */
  brand?: "spatialist" | "spacemanager" | "spacestation";
  /**
   * Visual tone. Controls opacity + animation but NOT image choice.
   *   - `default` — fully opaque (brand headers, heroes)
   *   - `subtle` — 70% opacity (secondary chrome, modal titles)
   *   - `watermark` — 30% opacity with a hover boost (corner marks)
   *   - `loading` — slow pulse (branded loaders)
   */
  variant?: "default" | "subtle" | "watermark" | "loading";
  /**
   * Pixel size. For `icon` this is both width and height. For `wordmark`
   * this is rendered height. For `horizontal` and `stacked` this is the
   * ICON height — the wordmark scales proportionally alongside it.
   */
  size?: number;
  className?: string;
  /**
   * Accessible label. Screen readers read this on the active image;
   * the inactive theme variant is marked aria-hidden so they're not
   * announced twice.
   */
  alt?: string;
  /**
   * Override theme resolution. Useful when the parent surface has a
   * FIXED color (e.g. the login page's always-dark left panel, a
   * dark-themed modal chrome) that doesn't follow the user's light/
   * dark preference. Defaults to `auto`, which tracks the `.dark` class
   * on <html> via Tailwind.
   */
  forceTheme?: "auto" | "light" | "dark";
  /**
   * When false, omit the lowercase `.xyz` suffix (rare — only when a
   * surface cannot render lowercase). Defaults to true.
   */
  withXyz?: boolean;
  /**
   * Next/Image priority preload. Defaults to false — the old behavior
   * (`variant === "default"`) preloaded the 48KB cube on nearly every
   * surface and contended with LCP text on `/` (Speed Insights). Opt in
   * only for branded loading screens where the mark is the primary paint.
   */
  priority?: boolean;
}

const TONE: Record<NonNullable<LogoMarkProps["variant"]>, string> = {
  default: "opacity-100",
  subtle: "opacity-70",
  watermark: "opacity-30 hover:opacity-60 transition-opacity",
  loading: "opacity-80 animate-pulse",
};

// Intrinsic aspect ratio of the Spatialist wordmark SVG (tight viewBox
// 114 54 372 46 → 372/46 ≈ 8.09). Used to compute rendered width from
// a height-only size prop so the browser doesn't layout-shift mid-
// paint. Keep in sync with the viewBox in public/brand/wordmark-*.svg.
const WORDMARK_ASPECT = 372 / 46;

// Founder-locked suffix recipe (2026-07-03 mockup pick: 63%).
// Height is a fraction of the wordmark cap-height. Width estimate is
// for optical-centering only (Nasalization is a wide display face;
// ".xyz" ≈ 2.5× its own height).
const XYZ_HEIGHT_RATIO = 0.63;
const XYZ_WIDTH_TO_HEIGHT = 2.5;
const XYZ_GAP_EM = 0.08;

// Size recipe for the composite lockups. `size` prop = icon height,
// everything else scales off that.
// - Wordmark height is 55% of icon height so cap-height sits visually
//   aligned with the cube's inner core (balanced, not dominated).
// - Gap is 20% of icon height for horizontal (room to breathe without
//   feeling loose), 10% for stacked (tighter vertical rhythm).
const HORIZONTAL_GAP_RATIO = 0.2;
const STACKED_GAP_RATIO = 0.1;
const WORDMARK_HEIGHT_RATIO_HORIZONTAL = 0.55;
const WORDMARK_HEIGHT_RATIO_STACKED = 0.42;

type ThemedWordmarkProps = {
  size: number;
  forceTheme: "auto" | "light" | "dark";
  tone: string;
  className?: string;
  alt?: string;
  withXyz?: boolean;
  /**
   * Apply the optical-centering shift (name on the container centerline,
   * `.xyz` hangs right). True for standalone `wordmark` and `stacked`
   * (the wordmark row is centered under the icon, so the suffix is the
   * only asymmetric weight); FALSE inside the `horizontal` lockup — the
   * icon+gap on the LEFT (~1.2×size) already counterweights the suffix
   * on the right (~0.87×size), leaving the name within ~4px of the true
   * centerline when the lockup is centered. Adding the shift there would
   * overshoot (~10px left of center) and mis-place left-aligned nav
   * lockups. Reviewed 2026-07-03 (Bugbot raised it; math says skip).
   */
  opticalCenter?: boolean;
};

function XyzSuffix({
  height,
  forceTheme,
}: {
  height: number;
  forceTheme: "auto" | "light" | "dark";
}) {
  const colorClass =
    forceTheme === "light"
      ? "text-black"
      : forceTheme === "dark"
        ? "text-white"
        : "text-black dark:text-white";

  return (
    <span
      aria-hidden="true"
      className={`normal-case ${colorClass}`}
      style={{
        fontFamily: "var(--font-display)",
        fontSize: `${height}px`,
        fontWeight: 400,
        lineHeight: 1,
        letterSpacing: "0.02em",
        marginLeft: `${XYZ_GAP_EM}em`,
      }}
    >
      .xyz
    </span>
  );
}

/**
 * SPACEMANAGER wordmark — dual-tone live Nasalization text (no SVG asset
 * exists for this brand). Same recipe as SPACESTATION: SPACE = logo
 * fill (`--vs-red`), MANAGER = black on light / white on dark. Height
 * maps `size` to the font cap-height area, so the three layouts' size
 * recipes keep working unchanged.
 */
function SpaceManagerWordmark({
  size,
  forceTheme,
  tone,
  className = "",
  alt,
}: Omit<ThemedWordmarkProps, "withXyz" | "opticalCenter">) {
  const managerColorClass =
    forceTheme === "light"
      ? "text-black"
      : forceTheme === "dark"
        ? "text-white"
        : "text-black dark:text-white";

  return (
    <span
      role={alt ? "img" : undefined}
      aria-label={alt || undefined}
      className={`inline-flex items-baseline uppercase ${tone} ${className}`.trim()}
      style={{
        fontFamily: "var(--font-display)",
        fontSize: `${size}px`,
        fontWeight: 400,
        lineHeight: 1,
        letterSpacing: "0.04em",
      }}
    >
      <span className="text-(--vs-red) [text-shadow:none]">SPACE</span>
      <span className={managerColorClass}>MANAGER</span>
    </span>
  );
}

/**
 * SPACESTATION wordmark — dual-tone live Nasalization text.
 * SPACE = logo fill (`--vs-red` / #C20000), not the neon dark-mode
 * text red — so it matches `logo-icon.png` sitting beside it.
 * STATION = black on light / white on dark. No `.xyz` (internal
 * display name, not a domain).
 */
function SpaceStationWordmark({
  size,
  forceTheme,
  tone,
  className = "",
  alt,
}: Omit<ThemedWordmarkProps, "withXyz" | "opticalCenter">) {
  const stationColorClass =
    forceTheme === "light"
      ? "text-black"
      : forceTheme === "dark"
        ? "text-white"
        : "text-black dark:text-white";

  return (
    <span
      role={alt ? "img" : undefined}
      aria-label={alt || undefined}
      className={`inline-flex items-baseline uppercase ${tone} ${className}`.trim()}
      style={{
        fontFamily: "var(--font-display)",
        fontSize: `${size}px`,
        fontWeight: 400,
        lineHeight: 1,
        letterSpacing: "0.04em",
      }}
    >
      <span className="text-(--vs-red) [text-shadow:none]">SPACE</span>
      <span className={stationColorClass}>STATION</span>
    </span>
  );
}

/**
 * Render the wordmark SVG (+ optional `.xyz`) with theme awareness.
 *
 * Uses plain <img> (not next/image) because:
 *   1. SVG doesn't benefit from next/image's raster optimization.
 *   2. next/image defaults reject SVG unless `dangerouslyAllowSVG` is
 *      set — not worth the config surface for a trusted in-repo file.
 *   3. Plain <img> with `loading="lazy"` covers our perf needs.
 *
 * Width is explicit so the browser reserves the right amount of inline
 * space before the SVG loads (prevents layout shift). Optical shift is
 * applied on the wrapper so call sites that center the mark stay
 * balanced without per-site CSS.
 */
function ThemedWordmark({
  size,
  forceTheme,
  tone,
  className = "",
  alt,
  withXyz = true,
  opticalCenter = true,
}: ThemedWordmarkProps) {
  const height = size;
  const width = Math.round(size * WORDMARK_ASPECT);
  const lightSrc = "/brand/wordmark-light.svg";
  const darkSrc = "/brand/wordmark-dark.svg";
  const xyzHeight = Math.round(size * XYZ_HEIGHT_RATIO);
  // Half the reserved suffix width — shifts the NAME onto the
  // container centerline; `.xyz` hangs to the right.
  const opticalShiftPx =
    withXyz && opticalCenter
      ? Math.round((xyzHeight * XYZ_WIDTH_TO_HEIGHT) / 2)
      : 0;

  const imgs =
    forceTheme === "light" ? (
      <img
        src={lightSrc}
        alt=""
        aria-hidden="true"
        width={width}
        height={height}
        className="shrink-0"
      />
    ) : forceTheme === "dark" ? (
      <img
        src={darkSrc}
        alt=""
        aria-hidden="true"
        width={width}
        height={height}
        className="shrink-0"
      />
    ) : (
      <>
        <img
          src={lightSrc}
          alt=""
          aria-hidden="true"
          width={width}
          height={height}
          className="shrink-0 dark:hidden"
        />
        <img
          src={darkSrc}
          alt=""
          aria-hidden="true"
          width={width}
          height={height}
          className="hidden shrink-0 dark:block"
        />
      </>
    );

  return (
    <span
      role={alt ? "img" : undefined}
      aria-label={alt || undefined}
      className={`inline-flex items-baseline ${tone} ${className}`.trim()}
      style={
        opticalShiftPx
          ? { transform: `translateX(-${opticalShiftPx}px)` }
          : undefined
      }
    >
      {imgs}
      {withXyz ? <XyzSuffix height={xyzHeight} forceTheme={forceTheme} /> : null}
    </span>
  );
}

export default function LogoMark({
  layout = "icon",
  variant = "default",
  size = 24,
  className = "",
  alt,
  forceTheme = "auto",
  withXyz = true,
  brand = "spatialist",
  priority = false,
}: LogoMarkProps) {
  const tone = TONE[variant];
  const eager = priority || variant === "loading";
  const resolvedAlt =
    alt ??
    (brand === "spacemanager"
      ? "SpaceManager"
      : brand === "spacestation"
        ? "SpaceStation"
        : "SPATIALIST.xyz");

  if (layout === "icon") {
    return (
      <Image
        src="/logo-icon.png"
        alt={resolvedAlt}
        width={size}
        height={size}
        priority={eager}
        className={`${tone} ${className}`.trim()}
      />
    );
  }

  if (layout === "wordmark") {
    if (brand === "spacemanager") {
      return (
        <SpaceManagerWordmark
          size={size}
          forceTheme={forceTheme}
          tone={tone}
          className={className}
          alt={resolvedAlt}
        />
      );
    }
    if (brand === "spacestation") {
      return (
        <SpaceStationWordmark
          size={size}
          forceTheme={forceTheme}
          tone={tone}
          className={className}
          alt={resolvedAlt}
        />
      );
    }
    return (
      <ThemedWordmark
        size={size}
        forceTheme={forceTheme}
        tone={tone}
        className={className}
        alt={resolvedAlt}
        withXyz={withXyz}
      />
    );
  }

  if (layout === "horizontal") {
    const iconSize = size;
    const gap = Math.round(size * HORIZONTAL_GAP_RATIO);
    const wordmarkHeight = Math.round(size * WORDMARK_HEIGHT_RATIO_HORIZONTAL);
    return (
      <span
        className={`inline-flex items-center ${tone} ${className}`.trim()}
        style={{ gap: `${gap}px` }}
      >
        <Image
          src="/logo-icon.png"
          alt={resolvedAlt}
          width={iconSize}
          height={iconSize}
          priority={eager}
          className="shrink-0"
        />
        {brand === "spacemanager" ? (
          <SpaceManagerWordmark
            size={wordmarkHeight}
            forceTheme={forceTheme}
            tone=""
            alt=""
          />
        ) : brand === "spacestation" ? (
          <SpaceStationWordmark
            size={wordmarkHeight}
            forceTheme={forceTheme}
            tone=""
            alt=""
          />
        ) : (
          <ThemedWordmark
            size={wordmarkHeight}
            forceTheme={forceTheme}
            tone=""
            alt=""
            withXyz={withXyz}
            opticalCenter={false}
          />
        )}
      </span>
    );
  }

  // stacked
  const iconSize = Math.round(size * 0.65);
  const gap = Math.round(size * STACKED_GAP_RATIO);
  const wordmarkHeight = Math.round(size * WORDMARK_HEIGHT_RATIO_STACKED);
  return (
    <span
      className={`inline-flex flex-col items-center ${tone} ${className}`.trim()}
      style={{ gap: `${gap}px` }}
    >
      <Image
        src="/logo-icon.png"
        alt={resolvedAlt}
        width={iconSize}
        height={iconSize}
        priority={eager}
        className="shrink-0"
      />
      {brand === "spacemanager" ? (
        <SpaceManagerWordmark
          size={wordmarkHeight}
          forceTheme={forceTheme}
          tone=""
          alt=""
        />
      ) : brand === "spacestation" ? (
        <SpaceStationWordmark
          size={wordmarkHeight}
          forceTheme={forceTheme}
          tone=""
          alt=""
        />
      ) : (
        <ThemedWordmark
          size={wordmarkHeight}
          forceTheme={forceTheme}
          tone=""
          alt=""
          withXyz={withXyz}
        />
      )}
    </span>
  );
}

/** Exported for tests / design docs — founder-locked 2026-07-03. */
export const LOGO_XYZ_HEIGHT_RATIO = XYZ_HEIGHT_RATIO;
